<template>
  <!--
    Génération du reçu de paiement.

    Un composant unique sert les deux cas d'usage :
      • le reçu d'UN versement (survol d'une ligne « Versement ») ;
      • le reçu global de la scolarité, quand tout est réglé.

    Le document est résolu avec les DONNÉES RÉELLES de l'élève, de sa classe,
    de la tranche et du versement concernés — rien n'est inventé.

    Téléporté en enfant direct de <body> : à l'impression, tout le reste de
    l'application peut alors être retiré du flux avec un simple
    `body > *:not(.rcp-gen-overlay)`, sans dépendre de l'endroit d'où le modal
    a été ouvert.
  -->
  <teleport to="body">
    <div v-if="open" class="rcp-gen-overlay" @click.self="close">
      <div class="rcp-gen-modal">
        <!-- ============================ En-tête ============================ -->
        <div class="gen-head rc-hide-print">
          <div class="head-top">
            <div>
              <h6 class="mb-1">{{ $t("payment_receipt_gen.title") }}</h6>
              <p class="mb-0 text-xs" style="opacity: 0.6">
                {{ contextLabel }}
              </p>
            </div>
            <button type="button" class="icon-close" @click="close">
              <i class="bi bi-x-lg"></i>
            </button>
          </div>

          <!--
            Choix du modèle pour CETTE impression seulement : la configuration
            de l'établissement n'est pas touchée. Chaque vignette est le
            document réel réduit, pas une icône — on voit ce qu'on imprimera.
          -->
          <div class="template-picker" v-if="sheet">
            <button
              type="button"
              class="pick"
              :class="{ active: activeTemplate === id }"
              v-for="id in templates"
              :key="id"
              :aria-pressed="activeTemplate === id"
              @click="selectTemplate(id)"
            >
              <span class="pick-thumb">
                <span class="pick-inner" :style="thumbStyle(id)">
                  <payment-receipt-sheet :data="thumbFor(id)" />
                </span>
              </span>
              <span class="pick-name">{{ $t(`payment_receipt.model_${id}`) }}</span>
            </button>
          </div>
        </div>

        <!-- ===================== Avertissement données ===================== -->
        <div class="gen-warning rc-hide-print" v-if="missingFields.length">
          <i class="bi bi-exclamation-triangle-fill me-2"></i>
          <div>
            <p class="mb-1 text-sm font-weight-bold">
              {{ $t("payment_receipt_gen.missing_title") }}
            </p>
            <p class="mb-0 text-xs" style="opacity: 0.75">
              {{ $t("payment_receipt_gen.missing_text", { fields: missingFields.join(", ") }) }}
            </p>
          </div>
        </div>

        <!-- ========================= Aperçu / rendu ========================= -->
        <div class="gen-body">
          <div class="rcp-print-area" v-if="sheet">
            <div class="gen-page" ref="page">
              <payment-receipt-sheet :data="sheet" />
            </div>
          </div>
          <p v-else class="text-center py-6" style="opacity: 0.6">
            {{ $t("payment_receipt_gen.empty") }}
          </p>
        </div>

        <!-- ============================ Actions ============================ -->
        <div class="gen-actions rc-hide-print">
          <button type="button" class="btn-ghost btn-edit" @click="goToConfig">
            <i class="bi bi-pencil-square me-1"></i>{{ $t("payment_receipt_gen.modify") }}
          </button>
          <button type="button" class="btn-ghost" @click="close">
            {{ $t("button.cancel") }}
          </button>
          <button type="button" class="btn-primary-soft" :disabled="!sheet" @click="print">
            <i class="bi bi-download me-1"></i>{{ $t("payment_receipt_gen.download") }}
          </button>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script>
import PaymentReceiptSheet from "@/views/Classroom/PaymentReceiptSheet.vue";
import { classroomSetup } from "@/utils/classroomSetup";
import {
  RECEIPT_PAGE_SIZES,
  RECEIPT_TEMPLATES,
  resolveGlobalReceipt,
  resolveReceipt,
} from "@/utils/receiptModel";
import { loadSchoolProfile, schoolProfile } from "@/utils/schoolProfile";
import api_config from "@/config";

export default {
  name: "payment-receipt-generator",
  components: { PaymentReceiptSheet },
  props: {
    open: { type: Boolean, default: false },
    /** Élève, tel que renvoyé par l'API (aucune transformation attendue). */
    student: { type: Object, default: () => ({}) },
    /** Classe : { name, number, speciality_code, grade_code }. */
    classroom: { type: Object, default: () => ({}) },
    /** Tranche concernée par le reçu (ignorée en mode global). */
    payment: { type: Object, default: null },
    /** Versement concerné. Absent → reçu de la tranche entière. */
    split: { type: Object, default: null },
    /** Rang du versement dans la tranche, pour le solde à cette date. */
    splitIndex: { type: Number, default: 0 },
    /**
     * Rang de la tranche (1-based), pour nommer « Tranche 2 » quand l'API ne
     * renvoie pas son numéro.
     */
    paymentIndex: { type: Number, default: 0 },
    /**
     * Reçu global de la scolarité : tous les montants sont ceux de
     * l'ensemble des tranches, et `payments` remplace `payment`.
     */
    global: { type: Boolean, default: false },
    payments: { type: Array, default: () => [] },
  },
  emits: ["close"],
  data() {
    return {
      school: schoolProfile,
      printMedia: null,
      templates: RECEIPT_TEMPLATES,
      /*
       * Modèle choisi pour CETTE impression. Nul tant qu'on n'a rien changé :
       * on suit alors la configuration de l'établissement. Ce choix ne
       * l'écrase pas — il faut passer par « Modifier » pour cela.
       */
      templateOverride: null,
    };
  },
  computed: {
    /** Modèle réellement imprimé : le choix ponctuel, sinon la configuration. */
    activeTemplate() {
      return this.templateOverride || classroomSetup.receipt.template;
    },
    receipt() {
      const saved = classroomSetup.receipt;
      if (saved.template === this.activeTemplate) return saved;
      return { ...saved, template: this.activeTemplate };
    },
    /** Le document résolu, prêt à imprimer. */
    sheet() {
      if (!this.open) return null;

      const common = {
        receipt: this.receipt,
        student: this.student,
        classroom: this.classroom,
        school: this.school,
        currency: api_config.CURRENCY_CODE,
        locale: this.$i18n.locale,
      };

      if (this.global) {
        return resolveGlobalReceipt({ ...common, payments: this.payments });
      }
      if (!this.payment) return null;
      return resolveReceipt({
        ...common,
        payment: this.payment,
        split: this.split,
        splitIndex: this.splitIndex,
        paymentIndex: this.paymentIndex,
      });
    },
    contextLabel() {
      if (!this.sheet) return "";
      return [this.sheet.student_name, this.sheet.type].filter(Boolean).join(" — ");
    },
    /** Champs réellement absents, signalés avant impression. */
    missingFields() {
      if (!this.sheet) return [];
      return this.sheet.missing.map((key) => this.$t(`payment_receipt_gen.field_${key}`));
    },
    /**
     * Format de page.
     *
     * Le modèle Ticket s'imprime sur un rouleau de 80 mm de large et de
     * hauteur libre ; les deux autres suivent le format choisi (A4 ou A5).
     */
    pageSetup() {
      const template = this.receipt.template;
      if (template === "ticket") return RECEIPT_PAGE_SIZES.ticket;
      return RECEIPT_PAGE_SIZES[this.receipt.format] || RECEIPT_PAGE_SIZES.a4;
    },
  },
  watch: {
    /*
     * Changer de modèle change le format de page (le Ticket sort en 80 mm) :
     * la feuille d'impression doit être refaite, sinon le format resterait
     * celui du modèle précédent.
     */
    activeTemplate() {
      if (this.open) this.$nextTick(this.refreshPrintStyle);
    },
    open: {
      immediate: true,
      handler(value) {
        // Le logo et le nom imprimés viennent de la fiche établissement :
        // sans ce chargement, le reçu sortirait sans en-tête.
        if (value) loadSchoolProfile();
        // On empêche le défilement de l'arrière-plan pendant l'aperçu.
        document.body.style.overflow = value ? "hidden" : "";
        if (value) {
          // Chaque ouverture repart du modèle configuré : un choix ponctuel
          // ne doit pas se reporter sur le reçu suivant.
          this.templateOverride = null;
          this.bindPrintEvents();
          /*
           * Le format de page est posé dès l'ouverture, et non au moment
           * d'imprimer. Le navigateur arrête la taille du papier lors du
           * premier calcul de mise en page, AVANT d'émettre `beforeprint` :
           * une règle `@page` installée depuis cet évènement arrivait trop
           * tard et l'aperçu restait au format par défaut. Après le rendu de
           * la feuille, dont les dimensions servent à la mesure.
           */
          this.$nextTick(this.refreshPrintStyle);
        } else {
          this.unbindPrintEvents();
        }
      },
    },
  },
  beforeUnmount() {
    document.body.style.overflow = "";
    this.unbindPrintEvents();
    this.removePrintStyle();
  },
  methods: {
    close() {
      this.$emit("close");
    },
    selectTemplate(template) {
      this.templateOverride = template;
    },
    /**
     * Document de la vignette : le reçu déjà résolu, dans un autre habillage.
     * On ne le recalcule pas — seul le modèle change, jamais les données.
     */
    thumbFor(template) {
      return this.sheet ? { ...this.sheet, template } : null;
    },
    /**
     * Réduction de la vignette. Le facteur dépend de la largeur réelle du
     * modèle : sans cela, le Ticket (320 px) et le Classique (768 px)
     * apparaîtraient à des échelles incohérentes.
     */
    thumbStyle(template) {
      const THUMB_WIDTH = 72;
      const width =
        template === "ticket" ? 320 : this.receipt.format === "a5" ? 576 : 768;
      return { width: `${width}px`, transform: `scale(${THUMB_WIDTH / width})` };
    },
    /** Ouvre la configuration du modèle, en mémorisant l'écran de départ. */
    goToConfig() {
      const params = this.$route.params || {};
      const kept = {};
      Object.keys(params).forEach((key) => {
        if (params[key] !== undefined && params[key] !== null) {
          kept[`p_${key}`] = String(params[key]);
        }
      });
      this.close();
      this.$router.push({
        name: "payment-receipt-config",
        query: { from: this.$route.name, ...kept },
      });
    },
    /**
     * Feuille de style d'impression, injectée dans <head>.
     *
     * `@page` ne peut pas être exprimé en style en ligne, et la taille de page
     * comme le facteur de réduction dépendent du modèle choisi : ils ne
     * peuvent donc pas vivre dans le bloc <style> statique du composant.
     *
     * Le nœud est REPLACÉ en fin de <head> à chaque impression. Une règle
     * `@page` ne peut être restreinte par aucun sélecteur : celles des écrans
     * de certificat (`size: A4 portrait`) s'appliquent donc à tout le
     * document dès que ces composants sont montés — et le profil élève monte
     * le générateur de certificats. Seul l'ordre de cascade départage, d'où
     * ce repositionnement.
     *
     * La feuille est réduite par `zoom` et non par `transform: scale()` : une
     * transformation ne change pas la boîte de mise en page. La feuille
     * continuait d'occuper sa largeur en pixels, plus large que la page
     * utile, et le navigateur repliait le document sur un A4 mis à l'échelle
     * par ses soins. `zoom` réduit la boîte elle-même, la feuille occupe
     * alors exactement la largeur utile du format choisi.
     */
    applyPrintStyle({ zoom, widthMm, pageWidth, pageHeight, margin }) {
      let node = document.getElementById("rcp-print-style");
      if (!node) {
        node = document.createElement("style");
        node.id = "rcp-print-style";
      }
      node.textContent = `@page { size: ${pageWidth}mm ${pageHeight}mm; margin: ${margin}mm; }
@media print {
  body.rcp-printing .gen-page {
    width: ${widthMm}mm !important;
    max-width: ${widthMm}mm !important;
    overflow: visible !important;
  }
  body.rcp-printing .gen-page .rc-sheet {
    zoom: ${zoom};
  }
}`;
      // appendChild sur un nœud déjà présent le déplace : c'est ce qui le
      // remet en dernier, après les feuilles des autres écrans.
      document.head.appendChild(node);
    },
    /** Mesure le document et pose la feuille de style correspondante. */
    refreshPrintStyle() {
      this.applyPrintStyle(this.computePrintFit());
    },
    removePrintStyle() {
      const node = document.getElementById("rcp-print-style");
      if (node && node.parentNode) node.parentNode.removeChild(node);
    },
    /**
     * Ajuste le document à la page choisie.
     *
     * La feuille est dessinée en pixels ; la page se mesure en millimètres.
     * On calcule donc le zoom qui fait tenir l'une dans l'autre, à partir de
     * la largeur utile réelle (format moins ses marges). La hauteur est
     * MESURÉE plutôt que supposée — le reçu s'allonge avec le nombre de
     * lignes — pour qu'il tienne sur une seule page.
     *
     * Renvoie aussi cette largeur utile en millimètres : c'est elle qui
     * dimensionne la page à l'impression.
     */
    computePrintFit() {
      const { width, height, margin } = this.pageSetup;
      const widthMm = width - margin * 2;
      const fallback = { zoom: 1, widthMm, pageWidth: width, pageHeight: height || 297, margin };

      const page = this.$refs.page;
      const sheet = page && page.firstElementChild;
      if (!sheet) return fallback;

      /* Millimètres -> pixels CSS (96 dpi). */
      const MM = 96 / 25.4;
      const availableWidth = widthMm * MM;
      const availableHeight = height ? (height - margin * 2) * MM : 0;

      const sheetWidth = sheet.offsetWidth || 768;
      const sheetHeight = sheet.offsetHeight || 0;

      const byWidth = availableWidth / sheetWidth;
      // Le rouleau du Ticket n'a pas de longueur imposée : seule la largeur
      // contraint la réduction.
      const byHeight = availableHeight && sheetHeight ? availableHeight / sheetHeight : Infinity;

      // Jamais d'agrandissement : un reçu tenant déjà dans la page garde sa
      // taille naturelle plutôt que d'être étiré.
      const zoom = Math.min(1, byWidth, byHeight);

      /*
       * Hauteur de la page. Pour A4 et A5 elle est imposée par le format ;
       * pour le rouleau du Ticket, elle est CALCULÉE à partir de la hauteur
       * réelle du reçu, qui varie avec le nombre de lignes.
       *
       * Elle ne peut pas rester indéterminée : `size: 80mm auto` mêle une
       * longueur et le mot-clé `auto`, ce que la syntaxe de `size` n'admet
       * pas. La déclaration entière était donc invalide, et le navigateur
       * retombait sur son format par défaut — l'A4 constaté à l'aperçu.
       */
      const pageHeight = height
        ? height
        : Math.ceil((sheetHeight * zoom) / MM) + margin * 2;

      return { zoom, widthMm, pageWidth: width, pageHeight, margin };
    },
    /**
     * Pose le marqueur qui active les règles @media print de ce composant.
     *
     * Déclenché par `beforeprint`, et non depuis le bouton : l'impression
     * peut aussi être lancée au clavier (Ctrl+P) ou par le menu du
     * navigateur. En passant par l'événement, tous les chemins sont couverts.
     */
    markPrinting() {
      // Dernière mesure : le document a pu changer de hauteur depuis
      // l'ouverture (chargement du logo, texte plus long).
      this.refreshPrintStyle();
      document.body.classList.add("rcp-printing");
    },
    unmarkPrinting() {
      document.body.classList.remove("rcp-printing");
    },
    /**
     * Safari n'émet pas `beforeprint` / `afterprint` : il signale l'entrée et
     * la sortie du mode impression via la media query.
     */
    onPrintMediaChange(event) {
      if (event.matches) this.markPrinting();
      else this.unmarkPrinting();
    },
    bindPrintEvents() {
      window.addEventListener("beforeprint", this.markPrinting);
      window.addEventListener("afterprint", this.unmarkPrinting);
      if (window.matchMedia) {
        this.printMedia = window.matchMedia("print");
        if (this.printMedia.addEventListener) {
          this.printMedia.addEventListener("change", this.onPrintMediaChange);
        }
      }
    },
    unbindPrintEvents() {
      window.removeEventListener("beforeprint", this.markPrinting);
      window.removeEventListener("afterprint", this.unmarkPrinting);
      if (this.printMedia && this.printMedia.removeEventListener) {
        this.printMedia.removeEventListener("change", this.onPrintMediaChange);
      }
      this.unmarkPrinting();
      // La règle `@page` s'applique au document entier : la laisser en place
      // imposerait le format du reçu à toute impression ultérieure.
      this.removePrintStyle();
    },
    /**
     * Ouvre la boîte d'impression du navigateur, d'où l'on enregistre en PDF.
     *
     * Le marqueur est posé par `beforeprint`. Aucun retrait programmé ici :
     * un `setTimeout` retirerait la classe pendant que l'aperçu d'impression
     * est encore ouvert, et l'aperçu, qui se met à jour en direct,
     * réafficherait alors toute la page derrière le reçu.
     */
    print() {
      // Repose le format juste avant d'ouvrir la boîte de dialogue : c'est le
      // dernier instant où le navigateur le lit encore.
      this.refreshPrintStyle();
      window.print();
    },
  },
};
</script>

<style scoped>
.rcp-gen-overlay {
  position: fixed;
  inset: 0;
  z-index: 1080;
  background: rgba(8, 12, 24, 0.72);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.rcp-gen-modal {
  /* Couleur posée explicitement : le modal est téléporté hors des cartes de
     l'application, il n'hérite donc d'aucune couleur de texte du thème. */
  color: #e6ebf5;
  width: min(100%, 900px);
  max-height: min(90vh, 940px);
  height: auto;
  display: flex;
  flex-direction: column;
  border-radius: 14px;
  background: #1b2138;
  border: 1px solid rgba(255, 255, 255, 0.12);
  overflow: hidden;
  box-shadow: 0 18px 50px rgba(0, 0, 0, 0.25);
}

.gen-head {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 16px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.09);
  flex-shrink: 0;
}

.head-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}

/* --- Choix du modèle ---------------------------------------------------- */

.template-picker {
  display: flex;
  gap: 10px;
}

.pick {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 6px 6px 7px;
  border-radius: 9px;
  border: 1px solid rgba(255, 255, 255, 0.14);
  background: rgba(255, 255, 255, 0.03);
  color: inherit;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;
}

.pick:hover {
  border-color: rgba(46, 206, 191, 0.5);
}

.pick.active {
  border-color: #2ecebf;
  background: rgba(46, 206, 191, 0.1);
}

/*
  La vignette garde la largeur réelle du modèle et n'est que mise à l'échelle :
  c'est une réduction fidèle du document, pas une version réagencée.
*/
.pick-thumb {
  display: block;
  width: 72px;
  height: 52px;
  overflow: hidden;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.04);
}

.pick-inner {
  display: block;
  transform-origin: top left;
  pointer-events: none;
}

.pick-name {
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
}

.icon-close {
  background: transparent;
  border: none;
  color: inherit;
  opacity: 0.6;
  cursor: pointer;
  font-size: 15px;
}

.icon-close:hover {
  opacity: 1;
}

.gen-warning {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin: 12px 20px 0;
  padding: 12px 14px;
  border-radius: 10px;
  background: rgba(251, 191, 36, 0.12);
  border: 1px solid rgba(251, 191, 36, 0.3);
  color: #fbbf24;
  flex-shrink: 0;
}

/*
  La feuille garde sa largeur réelle : la zone défile plutôt que de la
  réagencer, pour que l'aperçu corresponde exactement au document imprimé.
*/
.gen-body {
  flex: 1;
  overflow: auto;
  padding: 20px;
  background: rgba(0, 0, 0, 0.18);
}

.rcp-print-area {
  display: flex;
  justify-content: center;
  min-width: min-content;
}

.gen-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.09);
  flex-shrink: 0;
}

.btn-ghost {
  border-radius: 9px;
  border: 1px solid rgba(255, 255, 255, 0.16);
  background: transparent;
  color: inherit;
  font-size: 13px;
  padding: 9px 16px;
  cursor: pointer;
}

.btn-ghost:hover {
  border-color: rgba(255, 255, 255, 0.32);
}

/* Action secondaire, écartée des deux boutons de sortie pour ne pas se
   confondre avec eux. */
.btn-edit {
  margin-right: auto;
}

.btn-primary-soft {
  border: none;
  background: #2ecebf;
  color: #06231f;
  border-radius: 9px;
  padding: 9px 22px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.btn-primary-soft:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
</style>

<style>
/*
 * IMPRESSION — non scopé, car il faut atteindre <body> et les éléments de
 * l'application situés hors de ce composant.
 *
 * Trois points sensibles, identiques à ceux du générateur de certificats :
 *
 * 1. `!important` est indispensable sur .rcp-gen-overlay et .rcp-gen-modal :
 *    le <style scoped> ci-dessus compile en `.rcp-gen-overlay[data-v-xxx]`
 *    (spécificité 0,2,0) et l'emporterait sur ce bloc (0,1,0). L'overlay
 *    resterait `position: fixed`, or un élément fixed s'imprime tel qu'il
 *    apparaît à l'écran, sur une seule page.
 *
 * 2. Le reste de la page est retiré avec `display: none`, pas
 *    `visibility: hidden` : un élément seulement invisible occupe toujours sa
 *    place et produirait des pages blanches avant le reçu.
 *
 * 3. Tout est conditionné à `body.rcp-printing`, posé par `beforeprint`. Les
 *    blocs globaux des autres écrans (certificat) coexistent dans la même
 *    session : sans ce marqueur, chacun casserait l'impression de l'autre.
 */
@media print {
  body.rcp-printing {
    height: auto !important;
    overflow: visible !important;
    background: #ffffff !important;
  }

  body.rcp-printing > *:not(.rcp-gen-overlay) {
    display: none !important;
  }

  body.rcp-printing .rcp-gen-overlay {
    position: static !important;
    inset: auto !important;
    display: block !important;
    background: transparent !important;
    padding: 0 !important;
    overflow: visible !important;
    z-index: auto !important;
  }

  body.rcp-printing .rcp-gen-modal {
    width: 100% !important;
    max-width: none !important;
    max-height: none !important;
    height: auto !important;
    border: none !important;
    border-radius: 0 !important;
    box-shadow: none !important;
    background: transparent !important;
    overflow: visible !important;
    display: block !important;
  }

  body.rcp-printing .gen-head,
  body.rcp-printing .gen-warning,
  body.rcp-printing .gen-actions,
  body.rcp-printing .rc-hide-print {
    display: none !important;
  }

  body.rcp-printing .gen-body {
    padding: 0 !important;
    overflow: visible !important;
    min-height: 0 !important;
    flex: none !important;
    background: transparent !important;
  }

  body.rcp-printing .rcp-print-area {
    display: block !important;
    width: 100% !important;
    min-width: 0 !important;
  }

  /* Le reçu tient sur une page : jamais coupé en deux. */
  body.rcp-printing .gen-page {
    margin: 0 !important;
    padding: 0 !important;
    page-break-inside: avoid;
    break-inside: avoid;
  }

  /* .rc-sheet vient du <style scoped> de PaymentReceiptSheet.vue :
     `!important` est nécessaire pour passer devant. */
  body.rcp-printing .gen-page .rc-sheet {
    box-shadow: none !important;
    margin: 0 !important;
  }
}
</style>
